import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../component/layout";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { 
    getPropertyManagerContractsURL,
    getUnitsForFacility,
    getCustomersURL,
    getGLAccountsURL,
    getUsersURL,
    GET_DEPARTMENT_URL
} from "../../../../utils/urls";

// Import dialog components
import EditPropertyManagerContractDialog from "./EditPropertyManagerContract";
import TerminateContractDialog from "./TerminateContract";
import PropertyManagerContractDetailsDialog from "./PropertyManagerContractDetails";

const PropertyContractManagement = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    
    // Main state
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    
    // Dialog states
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showTerminateDialog, setShowTerminateDialog] = useState(false);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [selectedContract, setSelectedContract] = useState(null);
    
    // Data for dialogs
    const [propertyManagers, setPropertyManagers] = useState([]);
    const [units, setUnits] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [departments, setDepartments] = useState([]);

    // Filter options
    const statusOptions = [
        { label: 'All Status', value: '' },
        { label: 'Active', value: 'Active' },
        { label: 'Inactive', value: 'Inactive' },
        { label: 'Completed', value: 'Completed' },
        { label: 'Suspended', value: 'Suspended' },
        { label: 'Terminated', value: 'Terminated' }
    ];

    // Fetch departments
    const fetchDepartments = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${GET_DEPARTMENT_URL}/${facilityId}`, 'GET');
            
            if (response.success && response.data) {
                const departmentData = response.data.data || response.data;
                const departmentsArray = Array.isArray(departmentData) 
                    ? departmentData 
                    : [departmentData];
                setDepartments(departmentsArray);
                return departmentsArray;
            } else {
                setDepartments([]);
                return [];
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            setDepartments([]);
            return [];
        }
    };

    // Enhanced fetch property managers with proper department filtering
    const fetchPropertyManagers = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getUsersURL}/${facilityId}`, "GET");
            
            let usersData = [];
            if (Array.isArray(response)) {
                usersData = response;
            } else if (response && response.success && Array.isArray(response.data)) {
                usersData = response.data;
            } else if (response && Array.isArray(response.users)) {
                usersData = response.users;
            }

            // Filter users from Property Management department
            let propManagers = usersData.filter(user => {
                if (!user || !user.department || user.role !== "Staff") return false;
                
                if (typeof user.department === 'object' && user.department.name) {
                    return user.department.name === 'Property Management';
                }
                
                return false;
            });

            // If no managers found with populated department, try fetching departments
            if (propManagers.length === 0) {
                const departmentsData = await fetchDepartments();
                const propMgmtDept = departmentsData.find(dept => dept.name === 'Property Management');
                
                if (propMgmtDept) {
                    propManagers = usersData.filter(user => {
                        if (!user || !user.department || user.role !== "Staff") return false;
                        
                        if (typeof user.department === 'string') {
                            return user.department === propMgmtDept._id;
                        }
                        
                        if (typeof user.department === 'object' && user.department._id) {
                            return user.department._id === propMgmtDept._id;
                        }
                        
                        return false;
                    });
                }
            }

            // If still no property managers found, show all Staff users as fallback
            if (propManagers.length === 0) {
                propManagers = usersData.filter(user => 
                    user && 
                    user.role === "Staff" && 
                    user.facilityId === facilityId &&
                    user.isEnabled !== false
                );
            }
            
            setPropertyManagers(propManagers);
        } catch (error) {
            console.error("Error fetching property managers:", error);
            setPropertyManagers([]);
        }
    };

    // Fetch contracts data
    const fetchContracts = useCallback(async () => {
        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            
            if (!facilityId) {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: "No facility selected"
                });
                return;
            }

            const response = await makeRequest2(
                `${getPropertyManagerContractsURL}/${facilityId}`, 
                'GET'
            );

            // Handle makeRequest2's response structure
            let contractsData = null;
            
            if (response?.success && response.data) {
                if (response.data.data && response.data.data.contracts) {
                    contractsData = response.data.data;
                } else if (response.data.contracts) {
                    contractsData = response.data;
                } else {
                    contractsData = response.data;
                }
            } else if (response?.error && response.error.includes("200")) {
                contractsData = response.data || response;
                if (contractsData && contractsData.data && contractsData.data.contracts) {
                    contractsData = contractsData.data;
                }
            } else if (response?.data) {
                contractsData = response.data;
                if (contractsData && contractsData.data && contractsData.data.contracts) {
                    contractsData = contractsData.data;
                }
            }

            if (contractsData && contractsData.contracts) {
                const contractsList = contractsData.contracts || [];
                setContracts(contractsList);
                
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `${contractsList.length} contracts loaded successfully`
                });
            } else {
                setContracts([]);
                toast.current?.show({
                    severity: "info",
                    summary: "Info",
                    detail: "No contracts found"
                });
            }
        } catch (error) {
            console.error('Error fetching contracts:', error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Error fetching contracts: " + error.message
            });
            setContracts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch other data for dialogs
    const fetchUnits = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getUnitsForFacility}/${facilityId}`, "GET");
            
            let unitsData = [];
            if (Array.isArray(response)) {
                unitsData = response;
            } else if (response && response.success && Array.isArray(response.data)) {
                unitsData = response.data;
            } else if (response && Array.isArray(response.units)) {
                unitsData = response.units;
            }
            
            const unitsWithHomeOwners = unitsData.filter(unit => 
                unit && unit.homeOwnerId
            );
            
            setUnits(unitsWithHomeOwners);
        } catch (error) {
            console.error("Error fetching apartments:", error);
            setUnits([]);
        }
    };

    const fetchCustomers = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getCustomersURL}/${facilityId}`, "GET");
            
            let customersData = [];
            if (Array.isArray(response)) {
                customersData = response;
            } else if (response && response.success && Array.isArray(response.data)) {
                customersData = response.data;
            } else if (response && Array.isArray(response.customers)) {
                customersData = response.customers;
            }
            
            const homeowners = customersData.filter(customer => 
                customer && customer.customerType === 'home owner'
            );
            
            setCustomers(homeowners);
        } catch (error) {
            console.error("Error fetching customers:", error);
            setCustomers([]);
        }
    };

    const fetchGLAccounts = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");

            if (response && response.success) {
                const accountsData = response.data?.data || [];
                setGlAccounts(accountsData);
            } else {
                setGlAccounts([]);
            }
        } catch (error) {
            console.error("Error fetching GL accounts:", error);
            setGlAccounts([]);
        }
    };

    // Load all data for dialogs
    const loadDialogData = async () => {
        await Promise.all([
            fetchPropertyManagers(),
            fetchUnits(),
            fetchCustomers(),
            fetchGLAccounts()
        ]);
    };

    // Handle actions
    const handleViewContract = (contract) => {
        setSelectedContract(contract);
        setShowDetailsDialog(true);
    };

    const handleEditContract = async (contract) => {
        setSelectedContract(contract);
        if (propertyManagers.length === 0 || units.length === 0 || customers.length === 0) {
            await loadDialogData();
        }
        setShowEditDialog(true);
    };

    const handleTerminateContract = (contract) => {
        setSelectedContract(contract);
        setShowTerminateDialog(true);
    };

    const handleEditSuccess = () => {
        toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Contract updated successfully"
        });
        fetchContracts();
        setSelectedContract(null);
    };

    const handleTerminationSuccess = () => {
        toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Contract terminated successfully"
        });
        fetchContracts();
        setSelectedContract(null);
    };

    // Formatting functions
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return 'KES 0.00';
        const numAmount = Number(amount);
        if (isNaN(numAmount)) return 'KES 0.00';
        
        return `KES ${numAmount.toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // Column templates
    const contractNameTemplate = (rowData) => {
        return (
            <Link
                to="#"
                className="text-primary fw-bold"
                style={{ textDecoration: 'none' }}
                onClick={(e) => {
                    e.preventDefault();
                    handleViewContract(rowData);
                }}
            >
                {rowData.contractName || 'N/A'}
            </Link>
        );
    };

    const propertyManagerTemplate = (rowData) => {
        const pmDetails = rowData.propertyManagerDetails;
        
        if (!pmDetails) {
            return <span className="text-muted">No property manager</span>;
        }

        const isValid = pmDetails.isValidPropertyManager;
        
        return (
            <div>
                <div className={isValid ? 'text-dark' : 'text-warning'}>
                    {pmDetails.fullName || 'N/A'}
                </div>
                {!isValid && (
                    <small className="text-warning">
                        <i className="ti ti-alert-triangle me-1"></i>
                        Invalid department
                    </small>
                )}
            </div>
        );
    };

    const customerTemplate = (rowData) => {
        const customer = rowData.customer;
        if (!customer) return 'N/A';
        
        const customerName = `${customer.firstName} ${customer.lastName}`;
        
        // If we have a customer ID, make it clickable
        if (customer._id || customer.id) {
            const customerId = customer._id || customer.id;
            return (
                <Link 
                    to={`/facility/customer_management/view_customer/${customerId}`} 
                    style={{ color: 'royalblue', textDecoration: 'none' }}
                >
                    {customerName}
                </Link>
            );
        }
        
        return customerName;
    };

    const unitsTemplate = (rowData) => {
        const unitDetails = rowData.unitDetails || [];
        
        if (unitDetails.length === 0) {
            return 'No apartment assigned';
        }

        if (unitDetails.length === 1) {
            const unit = unitDetails[0];
            const unitName = unit.name || 'Apartment name unavailable';
            
            // If we have a unit ID, make it clickable
            if (unit._id || unit.id) {
                const unitId = unit._id || unit.id;
                return (
                    <Link
                        to={`/facility/unit_management/view_unit/${unitId}`}
                        style={{ color: 'royalblue', textDecoration: 'none' }}
                    >
                        {unitName}
                    </Link>
                );
            }
            
            return unitName;
        }

        if (unitDetails.length <= 3) {
            return unitDetails.map((unit, index) => {
                const unitName = unit.name || 'N/A';
                
                if (unit._id || unit.id) {
                    const unitId = unit._id || unit.id;
                    return (
                        <span key={index}>
                            <Link
                                to={`/facility/unit_management/view_unit/${unitId}`}
                                style={{ color: 'royalblue', textDecoration: 'none' }}
                            >
                                {unitName}
                            </Link>
                            {index < unitDetails.length - 1 ? ', ' : ''}
                        </span>
                    );
                }
                
                return <span key={index}>{unitName}{index < unitDetails.length - 1 ? ', ' : ''}</span>;
            });
        }

        // For more than 3 units, show first two as links + count
        const firstTwoUnits = unitDetails.slice(0, 2).map((unit, index) => {
            const unitName = unit.name || 'N/A';
            
            if (unit._id || unit.id) {
                const unitId = unit._id || unit.id;
                return (
                    <span key={index}>
                        <Link
                            to={`/facility/unit_management/view_unit/${unitId}`}
                            style={{ color: 'royalblue', textDecoration: 'none' }}
                        >
                            {unitName}
                        </Link>
                        {index < 1 ? ', ' : ''}
                    </span>
                );
            }
            
            return <span key={index}>{unitName}{index < 1 ? ', ' : ''}</span>;
        });
        
        const remainingCount = unitDetails.length - 2;
        
        return (
            <span>
                {firstTwoUnits} + {remainingCount} more
            </span>
        );
    };

    const statusTemplate = (rowData) => {
        const status = rowData.status || 'Unknown';
        
        const getSeverity = (status) => {
            switch (status?.toLowerCase()) {
                case "active":
                    return { color: "#4caf50", bgColor: "#e8f5e9" };
                case "inactive":
                    return { color: "#9e9e9e", bgColor: "#f5f5f5" };
                case "completed":
                    return { color: "#2196f3", bgColor: "#e3f2fd" };
                case "suspended":
                    return { color: "#ff9800", bgColor: "#fff3e0" };
                case "terminated":
                    return { color: "#f44336", bgColor: "#ffebee" };
                default:
                    return { color: "#9e9e9e", bgColor: "#f5f5f5" };
            }
        };

        const statusStyle = getSeverity(status);

        return (
            <span
                style={{
                    fontWeight: "bold",
                    color: statusStyle.color,
                    padding: "5px 10px",
                    borderRadius: "15px",
                    backgroundColor: statusStyle.bgColor,
                }}
            >
                {status}
            </span>
        );
    };

    const managementFeeTemplate = (rowData) => {
        const managementFee = rowData.managementFee;
        
        if (!managementFee) return 'N/A';
        
        if (managementFee.type === 'percentage') {
            return `${managementFee.value}%`;
        } else if (managementFee.type === 'amount') {
            return formatCurrency(managementFee.value);
        }
        
        return 'N/A';
    };

    const actionsTemplate = (rowData) => {
        const isTerminated = rowData.status === 'Terminated';
        
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-info p-button-sm"
                    tooltip="View Details"
                    onClick={() => handleViewContract(rowData)}
                />
                
                {!isTerminated && (
                    <>
                        <Button
                            icon="ti ti-edit"
                            className="p-button-rounded p-button-warning p-button-sm"
                            tooltip="Edit Contract"
                            onClick={() => handleEditContract(rowData)}
                        />
                        
                        <Button
                            icon="ti ti-trash"
                            className="p-button-rounded p-button-danger p-button-sm"
                            tooltip="Terminate Contract"
                            onClick={() => handleTerminateContract(rowData)}
                        />
                    </>
                )}
            </div>
        );
    };

    // Filter contracts based on search and status
    const filteredContracts = contracts.filter((contract) => {
        const searchableText = [
            contract.contractName,
            contract.propertyManagerDetails?.fullName,
            contract.customer?.firstName,
            contract.customer?.lastName,
            contract.status,
            ...(contract.unitDetails || []).map(unit => unit.name)
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        const matchesSearch = searchKeyword ?
            searchableText.includes(searchKeyword.toLowerCase()) :
            true;

        const matchesStatus = statusFilter ?
            contract.status === statusFilter :
            true;

        return matchesSearch && matchesStatus;
    });

    const clearFilters = () => {
        setSearchKeyword("");
        setStatusFilter("");
    };

    // Load data on component mount
    useEffect(() => {
        fetchContracts();
    }, [fetchContracts]);


    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="">Property Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    Property Manager Contracts
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5>Property Manager Contracts</h5>
                </div>
                <div className="card-body">
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    {/* Filters */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <input
                                className="form-control"
                                placeholder="Search contracts..."
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <select
                                className="form-control"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="Completed">Completed</option>
                                <option value="Suspended">Suspended</option>
                                <option value="Terminated">Terminated</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <Button
                                label="Clear"
                                icon="pi pi-times"
                                onClick={clearFilters}
                                className="p-button-outlined p-button-secondary me-2"
                            />
                        </div>
                        <div className="col-md-4 text-end">
                            <Button
                                label="Create Contract"
                                icon="ti ti-plus"
                                className="p-button-primary"
                                onClick={() => navigate("/facility/property_management/property_manager_contract")}
                            />
                        </div>
                    </div>

                    <DataTable
                        value={filteredContracts}
                        loading={loading}
                        paginator
                        rows={10}
                        emptyMessage="No contracts found"
                        sortMode="multiple"
                        removableSort
                        stripedRows
                        tableStyle={{ minWidth: '70rem' }}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} contracts"
                        rowsPerPageOptions={[10, 25, 50]}
                                    >
                                        <Column 
                                            field="contractName" 
                                            header="Contract" 
                                            sortable 
                                            body={contractNameTemplate}
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column 
                                            field="propertyManagerDetails.fullName" 
                                            header="Property Manager" 
                                            body={propertyManagerTemplate}
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column 
                                            field="customer" 
                                            header="Client" 
                                            body={customerTemplate}
                                            style={{ minWidth: '150px' }}
                                        />
                                        <Column 
                                            field="unitDetails" 
                                            header="Apartments" 
                                            body={unitsTemplate}
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column 
                                            field="status" 
                                            header="Status" 
                                            body={statusTemplate}
                                            sortable 
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column 
                                            field="startDate" 
                                            header="Start Date" 
                                            body={(rowData) => formatDate(rowData.startDate)}
                                            sortable 
                                            style={{ minWidth: '110px' }}
                                        />
                                        <Column 
                                            field="endDate" 
                                            header="End Date" 
                                            body={(rowData) => formatDate(rowData.endDate)}
                                            sortable 
                                            style={{ minWidth: '110px' }}
                                        />
                                        <Column 
                                            field="managementFee" 
                                            header="Fee" 
                                            body={managementFeeTemplate}
                                            sortable 
                                            style={{ minWidth: '80px' }}
                                        />
                                        <Column 
                                            header="Actions" 
                                            body={actionsTemplate}
                                            style={{ minWidth: '150px' }}
                                        />
                                    </DataTable>
                </div>
            </div>

            {/* Contract Details Dialog */}
            <PropertyManagerContractDetailsDialog
                visible={showDetailsDialog}
                onHide={() => setShowDetailsDialog(false)}
                contractData={selectedContract}
                contractId={selectedContract?._id}
            />

            {/* Edit Contract Dialog */}
            <EditPropertyManagerContractDialog
                visible={showEditDialog}
                onHide={() => setShowEditDialog(false)}
                contractData={selectedContract}
                onEditSuccess={handleEditSuccess}
                propertyManagers={propertyManagers}
                units={units}
                customers={customers}
                glAccounts={glAccounts}
            />

            {/* Terminate Contract Dialog */}
            <TerminateContractDialog
                visible={showTerminateDialog}
                onHide={() => setShowTerminateDialog(false)}
                contractData={selectedContract}
                onTerminationSuccess={handleTerminationSuccess}
            />
        </Layout>
    );
};

export default PropertyContractManagement;